<template>
    <div class="flex flex-col h-screen">
        <div class="flex-1 p-4 overflow-y-auto">
            <div class="text-gray text-sm mb-4">Создание плана {{ dt }}</div>
            <div class="flex mb-5">
                <Avatar :first-name="schedule.user.first_name" :last-name="schedule.user.last_name" size="md" :url="schedule.user.avatar" />
                <div class="ml-4">
                    <div class="text-sm semibold text-darkgray">{{ schedule.user.last_name }} {{ schedule.user.first_name }} {{
                        schedule.user.middle_name }}
                    </div>
                    <div class="text-xs text-gray">{{ schedule.position?.name }}</div>
                    <div class="text-xs text-gray">{{ schedule.department.name }}</div>
                </div>
            </div>

            <EmployeeStats class="mb-3" :schedule />

            <Shifts ref="shiftsRef" :schedule :date="dt" class="mb-4" @switch="emit('switch')" />

            <ShiftsCopy :dt v-if="showCopyAndHistory" @on-copy="onCopyDatesChange" />

            <ShiftHistory v-if="savedShifts.length && showShiftHistory" />
        </div>

        <div class="text-right pb-4">
            <btn class="mr-5" @click="saveShifts()" :disabled="!canSave" :class="{ 'opacity-50': !canSave }">Сохранить</btn>
        </div>
    </div>

    <ShiftCopyModal
        v-if="copyProcess.isModalOpen.value"
        :mode="copyProcess.modalMode.value"
        :errors="copyProcess.modalErrors.value"
        :preview="copyProcess.preview.value"
        :is-loading="copyProcess.isLoading.value"
        @close="copyProcess.close"
        @confirm="copyProcess.confirm"
    />
</template>

<script setup lang="ts">
interface Props {
    scheduleIndex: number,
    day: number
}
import { useScheduleStore } from '@/stores/scheduleStore'
import { onMounted, computed, onUnmounted, ref, watch } from 'vue'
import Avatar from '../Avatar.vue'
import EmployeeStats from '../employee/EmployeeStats.vue'
import dayjs from "dayjs"
import Shifts from './Shifts.vue'
import ShiftsCopy from './ShiftsCopy.vue'
import ShiftHistory from './ShiftHistory.vue'
import { useAuthStore } from '@/stores/authStore'
import ShiftCopyModal from './ShiftCopyModal.vue'
import { useShiftCopy } from './useShiftCopy'

const emit = defineEmits(['close', 'switch'])

const store = useScheduleStore()
const authStore = useAuthStore()
const shiftsRef = ref()
const copyTargetDates = ref<string[]>([])
const copyProcess = useShiftCopy()

dayjs.locale('ru')

const { scheduleIndex, day } = defineProps<Props>()

const dt = computed(() => dayjs(store.currentScheduleDate).date(day).format('YYYY-MM-DD'))

const schedule = computed(() => store.schedules[scheduleIndex])

const showShiftHistory = computed(() => authStore.permissions?.can_view_shift_history)

const shifts = computed(() => schedule.value.shifts.filter(shift => +dayjs(shift.date).format('D') === day))
const savedShifts = computed(() => shifts.value.filter(item => item.id))
const hasDayStateDocumentInCurrentDay = computed(() => store.currentShifts.some((shift) => Boolean(shift?.day_state_document)))
const showCopyAndHistory = computed(() => (
    authStore.permissions?.can_create_schedule
    && dayjs(dt.value).isAfter(dayjs(), 'day')
    && !!savedShifts.value.length
    && !store.changedShifts.length
    && !store.shiftFieldsClicked
    && !hasDayStateDocumentInCurrentDay.value
))
const canSave = computed(() => store.isSaveShiftEnabled || copyTargetDates.value.length > 0)

async function saveShifts() {
    if (copyTargetDates.value.length && store.currentDepartment?.id && store.currentShift?.employment) {
        await copyProcess.start({
            department_id: store.currentDepartment.id,
            employment_ids: [store.currentShift.employment],
            source_dates: [dt.value],
            target_dates: [...copyTargetDates.value],
        }, () => {
            copyTargetDates.value = []
            emit('close')
        })
        return
    }

    if (!validateCurrentShiftPlanTime()) return

    const savingShifts = [store.currentShift]
    if(savingShifts.length) {
        store.isSaveShiftEnabled = false
        await store.shiftPackCreateUpdate(savingShifts)
        store.isCurrentShiftChanged = false
        emit('close')
    } else {
        store.isSaveShiftEnabled = false
    }
}

const onCopyDatesChange = (targetDates: string[]) => {
    copyTargetDates.value = targetDates
}

watch(showCopyAndHistory, (isVisible) => {
    if (!isVisible) copyTargetDates.value = []
})

onMounted(() => {
    const originalShifts = JSON.parse(JSON.stringify(shifts.value))
    store.setCurrentShifts([...shifts.value])
    store.setOriginalShifts(originalShifts)
    if (shifts.value.length) {
        store.setCurrentShift({...shifts.value[0]})
    }
})

onUnmounted(() => {
    store.setCurrentShifts([])
    store.setCurrentShift(null)
    store.changedShifts = []
    store.shiftFieldsClicked = false
})

const completePendingShiftSwitch = () => {
    shiftsRef.value?.completePendingSwitch()
}

const cancelPendingShiftSwitch = () => {
    shiftsRef.value?.cancelPendingSwitch()
}

const validateCurrentShiftPlanTime = () => {
    return shiftsRef.value?.validateCurrentShiftPlanTime() ?? true
}

defineExpose({
    completePendingShiftSwitch,
    cancelPendingShiftSwitch,
    validateCurrentShiftPlanTime
})
</script>
